import { Injectable } from '@angular/core';
import { Notification, Novu, WebSocketEvent } from '@novu/js';
import { environment } from 'src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from 'src/services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private unreadCount = new BehaviorSubject<number>(0);
  private notificationsData = new BehaviorSubject<Notification[]>([]);

  public unreadCount$ = this.unreadCount.asObservable();
  public notifications$ = this.notificationsData.asObservable();

  private novu?: Novu;
  private initialized = false;

  constructor(private authService: AuthService) {
    // Do not initialize Novu here! Initialization is handled in initializeForUser.
  }

  get notifications() {
    return this.novu?.notifications;
  }

  initializeForUser(userId: string, userHash?: string) {
    if (!userId) {
      this.initialized = false;
      this.novu = undefined;
      return;
    }
    try {
      if (this.initialized) {
        this.cleanup();
      }
      console.log('Initializing Novu with userId:', userId);
      this.novu = new Novu({
        applicationIdentifier: environment.novu?.applicationIdentifier || '',
        subscriberId: userId,
        subscriberHash: userHash,
      });
      this.initialized = true;
      this.setupEventListeners();
      this.fetchCounts();
      this.fetchNotificationsList();
      this.connectSocket();
    } catch (error) {
      console.error('Error initializing Novu for user:', error);
      this.initialized = false;
    }
  }

  async fetchCounts() {
    if (!this.initialized || !this.novu) return;
    try {
      const response = await this.novu.notifications.count({
        read: false,
        archived: false,
      });
      if (response && response.data) {
        this.unreadCount.next(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  }

  async fetchNotificationsList() {
    if (!this.initialized || !this.novu) return [];
    try {
      await this.novu.notifications.clearCache();
      const response = await this.novu.notifications.list();
      if (response && response.data && Array.isArray(response.data.notifications)) {
        this.notificationsData.next(response.data.notifications);
        const unreadCount = response.data.notifications.filter(
          (notification: any) => !notification.isRead
        ).length;
        this.unreadCount.next(unreadCount);
        return response.data.notifications;
      }
      return [];
    } catch (error) {
      console.error('Error fetching notifications list:', error);
      return [];
    }
  }

  private setupEventListeners() {
    if (!this.novu || !this.initialized) return;

    // Add new notification(s) directly to the list, do not refetch
    this.novu.on(`notifications.${WebSocketEvent.RECEIVED}` as any, (data: Notification | Notification[]) => {
      const current = this.notificationsData.getValue();
      const newNotifications = Array.isArray(data) ? data : [data];
      const updated = [...newNotifications, ...current];
      this.notificationsData.next(updated);
      const unreadCount = updated.filter((n: any) => !n.isRead).length;
      this.unreadCount.next(unreadCount);
    });

    // Update unread count if event received
    this.novu.on(`notifications.${WebSocketEvent.UNREAD}` as any, (data: any) => {
      if (data && data.result !== undefined) {
        this.unreadCount.next(data.result);
      }
    });

    // On socket connect, refresh counts and notifications
    this.novu.on('socket.connect.resolved' as any, () => {
      this.fetchCounts();
      this.fetchNotificationsList();
    });
  }

  async connectSocket() {
    if (!this.initialized || !this.novu) {
      return Promise.reject(new Error('Novu not initialized'));
    }
    try {
      await this.novu.socket.connect();
    } catch (error) {
      console.error('Error connecting to Novu socket:', error);
      return Promise.reject(error);
    }
  }

  async disconnectSocket() {
    if (!this.initialized || !this.novu) {
      return Promise.resolve();
    }
    try {
      return await this.novu.socket.disconnect();
    } catch (error) {
      console.error('Error disconnecting from Novu socket:', error);
      return Promise.reject(error);
    }
  }

  private cleanup() {
    this.disconnectSocket();
  }

  reset() {
    this.cleanup();
    this.unreadCount.next(0);
    this.notificationsData.next([]);
    this.initialized = false;
  }
}